import classNames from 'classnames'
import { FC, useState } from 'react'
import {
  CitationType,
  DeepDive,
} from 'src/services/aiListeningApi/brandProfile.types'
import styles from './deepDive.module.scss'
import { Pill } from '../shared/Pill'
import { displayUrl, formatNumber, formatPercent } from '../shared/utils'

type Group = DeepDive['citations']['groups'][CitationType]
type DomainItem = Group['items'][number]

const VISIBLE_DOMAINS = 5
const VISIBLE_PAGES = 3

const GROUPS: { title: string; type: CitationType }[] = [
  { title: 'Owned', type: 'owned' },
  { title: 'Competitor-Owned', type: 'competitor' },
  { title: 'Third-Party', type: 'third_party' },
]

const GlobeIcon: FC = () => (
  <svg fill="none" height="16" viewBox="0 0 16 16" width="16">
    <circle cx="8" cy="8" r="6.2" stroke="currentColor" strokeWidth="1.2" />
    <path
      d="M1.8 8h12.4M8 1.8c1.6 1.7 2.4 3.8 2.4 6.2S9.6 12.5 8 14.2C6.4 12.5 5.6 10.4 5.6 8S6.4 3.5 8 1.8z"
      stroke="currentColor"
      strokeWidth="1.2"
    />
  </svg>
)

const LinkIcon: FC = () => (
  <svg fill="none" height="14" viewBox="0 0 14 14" width="14">
    <path
      d="M5.8 8.2l2.4-2.4M6.6 3.6l.9-.9a2.3 2.3 0 0 1 3.3 3.3l-.9.9M7.4 10.4l-.9.9a2.3 2.3 0 0 1-3.3-3.3l.9-.9"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="1.2"
    />
  </svg>
)

interface DomainCardProps {
  item: DomainItem
  maxCitations: number
}

const DomainCard: FC<DomainCardProps> = ({ item, maxCitations }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [showAllPages, setShowAllPages] = useState(false)
  const pages = showAllPages ? item.pages : item.pages.slice(0, VISIBLE_PAGES)
  const hiddenPages = item.pages.length - VISIBLE_PAGES

  return (
    <div
      className={classNames(styles.domainCard, {
        [styles.domainCardOpen]: isOpen,
      })}
    >
      <button
        aria-expanded={isOpen}
        className={styles.domainHeader}
        onClick={() => setIsOpen((open) => !open)}
        type="button"
      >
        <GlobeIcon />
        <span className={styles.domainName}>{item.domain}</span>
        <span className={styles.muted}>{formatNumber(item.citations)}</span>
        <svg
          className={classNames(styles.chevron, {
            [styles.chevronOpen]: isOpen,
          })}
          fill="none"
          height="16"
          viewBox="0 0 16 16"
          width="16"
        >
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.4"
          />
        </svg>
      </button>
      <span className={styles.domainBar}>
        <span
          style={{
            width: `${maxCitations ? (item.citations / maxCitations) * 100 : 0}%`,
          }}
        />
      </span>
      {isOpen && (
        <div className={styles.domainPages}>
          {pages.map((page) => (
            <a
              className={styles.domainPage}
              href={page.url}
              key={page.url}
              rel="noopener noreferrer"
              target="_blank"
            >
              <LinkIcon />
              <span className={styles.ellipsis}>{displayUrl(page.url)}</span>
              <span className={styles.domainPageCount}>{page.citations}</span>
            </a>
          ))}
          {!showAllPages && hiddenPages > 0 && (
            <button
              className={styles.linkButton}
              onClick={() => setShowAllPages(true)}
              type="button"
            >
              + {hiddenPages} more {hiddenPages === 1 ? 'page' : 'pages'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

interface GroupColumnProps {
  domain: string
  group: Group
  ownedGap: boolean
  title: string
  type: CitationType
}

const GroupColumn: FC<GroupColumnProps> = ({
  domain,
  group,
  ownedGap,
  title,
  type,
}) => {
  const [showAll, setShowAll] = useState(false)
  const items = showAll ? group.items : group.items.slice(0, VISIBLE_DOMAINS)
  const hidden = group.items.length - VISIBLE_DOMAINS
  const maxCitations = Math.max(...group.items.map((item) => item.citations), 0)

  return (
    <div className={styles.citationColumn}>
      <div className={styles.citationColumnHead}>
        <span className={styles.citationColumnTitle}>
          <span
            className={classNames(styles.square, styles[`square_${type}`])}
          />
          {title}
        </span>
        <span className={styles.countBadge}>{group.domains}</span>
      </div>
      <span className={styles.citationColumnMeta}>
        {group.domains} {group.domains === 1 ? 'domain' : 'domains'} ·{' '}
        {group.citations} {group.citations === 1 ? 'citation' : 'citations'}
      </span>
      {type === 'owned' && ownedGap && (
        <div className={styles.gapCard}>
          <span className={styles.gapHead}>
            <GlobeIcon />
            <span className={styles.domainName}>
              {domain || 'Your website'}
            </span>
            <span className={styles.gapBadge}>Citation gap</span>
          </span>
          <span>0 citations · 0 models</span>
        </div>
      )}
      {items.map((item) => (
        <DomainCard item={item} key={item.domain} maxCitations={maxCitations} />
      ))}
      {group.items.length === 0 && !(type === 'owned' && ownedGap) && (
        <span className={styles.muted}>No domains cited</span>
      )}
      {hidden > 0 && (
        <button
          className={styles.moreDomains}
          onClick={() => setShowAll((value) => !value)}
          type="button"
        >
          {showAll ? 'Show fewer domains' : `Show ${hidden} more domains`}
          <svg
            className={classNames(styles.chevron, {
              [styles.chevronOpen]: showAll,
            })}
            fill="none"
            height="16"
            viewBox="0 0 16 16"
            width="16"
          >
            <path
              d="M4 6l4 4 4-4"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.4"
            />
          </svg>
        </button>
      )}
    </div>
  )
}

interface CitationIntelligenceProps {
  brandName: string
  citations: DeepDive['citations']
  domain: string
}

export const CitationIntelligence: FC<CitationIntelligenceProps> = ({
  brandName,
  citations,
  domain,
}) => {
  const { groups } = citations
  const totalDomains =
    groups.owned.domains +
    groups.competitor.domains +
    groups.third_party.domains

  return (
    <section className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <h2 className={styles.cardTitleLg}>
            Citation Intelligence &amp; Domain Authority
          </h2>
          <p className={styles.cardSubtitle}>
            Understand which sources influence AI visibility and where citation
            gaps exist
          </p>
        </div>
        <Pill outline size="md" tone="amber">
          {formatPercent(citations.ownedShare)} Owned Share
        </Pill>
      </div>

      <div className={styles.breakdownRow}>
        <div className={styles.breakdown}>
          <span className={styles.blockTitle}>Citation Source Breakdown</span>
          <span className={styles.breakdownBar}>
            <span
              className={styles.square_owned}
              style={{ width: `${citations.ownedShare}%` }}
            />
            <span
              className={styles.square_competitor}
              style={{ width: `${citations.competitorShare}%` }}
            />
          </span>
          <span className={styles.breakdownLegend}>
            <span>
              <span
                className={classNames(styles.square, styles.square_owned)}
              />
              {brandName} ({formatPercent(citations.ownedShare)})
            </span>
            <span>
              <span
                className={classNames(styles.square, styles.square_competitor)}
              />
              Competitor ({formatPercent(citations.competitorShare)})
            </span>
            <span>
              <span
                className={classNames(styles.square, styles.square_third_party)}
              />
              Third-Party ({formatPercent(citations.thirdPartyShare)})
            </span>
          </span>
        </div>
        <div className={styles.countTiles}>
          <div className={styles.countTile}>
            <span>Total Source</span>
            <strong>{totalDomains}</strong>
          </div>
          <div className={styles.countTile}>
            <span>Owned</span>
            <strong
              className={groups.owned.domains === 0 ? styles.bad : undefined}
            >
              {groups.owned.domains}
            </strong>
          </div>
          <div className={styles.countTile}>
            <span>Competitor-Owned</span>
            <strong>{groups.competitor.domains}</strong>
          </div>
          <div className={styles.countTile}>
            <span>Third-Party</span>
            <strong>{groups.third_party.domains}</strong>
          </div>
        </div>
      </div>

      <div className={styles.citationColumns}>
        {GROUPS.map(({ title, type }) => (
          <GroupColumn
            domain={domain}
            group={groups[type]}
            key={type}
            ownedGap={citations.ownedGap}
            title={title}
            type={type}
          />
        ))}
      </div>
    </section>
  )
}
